"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_ROADMAP_PRIORITY = void 0;
exports.getWeaveStatus = getWeaveStatus;
exports.getWeavePhase = getWeavePhase;
exports.isPlanStale = isPlanStale;
exports.getStalePlans = getStalePlans;
exports.isReqStale = isReqStale;
exports.staleEntries = staleEntries;
exports.getThreadStatus = getThreadStatus;
exports.buildRoadmap = buildRoadmap;
const dates_1 = require("./dates");
const versionUtils_1 = require("./versionUtils");
/**
 * ctx, reference, and req docs are perpetual context, not workstream
 * deliverables — they carry a non-`done` status forever (a locked req is the
 * thread's standing spec), so counting them in the every-done check would
 * permanently block a weave/thread from reaching DONE. Exclude them.
 */
function isDeliverable(doc) {
    return doc.type !== 'ctx' && doc.type !== 'reference' && doc.type !== 'req' && doc.type !== 'thread';
}
function getWeaveStatus(weave) {
    const plans = weave.threads.flatMap(t => t.plans);
    const deliverables = weave.allDocs.filter(isDeliverable);
    if (plans.some(p => p.status === 'implementing'))
        return 'IMPLEMENTING';
    if (deliverables.length > 0 && deliverables.every(d => d.status === 'done'))
        return 'DONE';
    if (plans.some(p => p.status === 'active' || p.status === 'draft'))
        return 'ACTIVE';
    if (plans.some(p => p.status === 'blocked'))
        return 'BLOCKED';
    return 'ACTIVE';
}
function getWeavePhase(weave) {
    const plans = weave.threads.flatMap(t => t.plans);
    const hasDesign = weave.threads.some(t => t.design != null);
    if (plans.some(p => p.status === 'implementing' || p.status === 'done'))
        return 'implementing';
    if (plans.length > 0)
        return 'planning';
    if (hasDesign)
        return 'designing';
    return 'ideating';
}
function isPlanStale(plan, design) {
    return plan.design_version < design.version;
}
function getStalePlans(weave) {
    return weave.threads.flatMap(thread => {
        if (!thread.design)
            return [];
        return thread.plans.filter(p => isPlanStale(p, thread.design));
    });
}
/**
 * A downstream doc (idea/design/plan) is req-stale when the thread's req is
 * locked at a version newer than the `req_version` the doc was built against.
 * A doc with no `req_version` has no known baseline — never flagged (so legacy
 * docs and docs authored before a req existed are not false-positived).
 */
function isReqStale(doc, req) {
    if (!req || req.status !== 'locked')
        return false;
    if (typeof doc.req_version !== 'number')
        return false;
    return doc.req_version < req.version;
}
/** Stale work that no longer needs action because the doc itself is finished. */
function isInactiveStatus(status) {
    return status === 'done' || status === 'cancelled';
}
/**
 * Every staleness hit in a weave, in one pass. Pure, directional, version-based.
 * The `actionable` flag is carried (never pre-filtered) so the unfiltered view
 * sees exactly the same entries the actionable surfaces hide — the two can't drift.
 */
function staleEntries(weave) {
    const out = [];
    for (const thread of weave.threads) {
        const { idea, design, req, plans } = thread;
        // design <- idea
        if (design && idea && typeof design.idea_version === 'number' && design.idea_version < idea.version) {
            out.push({
                docId: design.id, type: 'design', weaveId: weave.id, threadId: thread.id,
                reason: 'design_stale',
                detail: `idea v${idea.version} > design baseline v${design.idea_version}`,
                actionable: !isInactiveStatus(design.status) && design.status !== 'closed',
            });
        }
        // req <- design
        if (req && design && typeof req.design_version === 'number' && req.design_version < design.version) {
            out.push({
                docId: req.id, type: 'req', weaveId: weave.id, threadId: thread.id,
                reason: 'req_stale',
                detail: `design v${design.version} > req baseline v${req.design_version}`,
                actionable: true, // a req is never done/cancelled
            });
        }
        // plan <- design
        if (design) {
            for (const plan of plans) {
                if (plan.design_version < design.version) {
                    out.push({
                        docId: plan.id, type: 'plan', weaveId: weave.id, threadId: thread.id,
                        reason: 'plan_design_stale',
                        detail: `design v${design.version} > plan baseline v${plan.design_version}`,
                        actionable: !isInactiveStatus(plan.status),
                    });
                }
            }
        }
        // plan <- req (only a locked req is a stable spec; isReqStale gates on that)
        if (req) {
            for (const plan of plans) {
                if (isReqStale(plan, req)) {
                    out.push({
                        docId: plan.id, type: 'plan', weaveId: weave.id, threadId: thread.id,
                        reason: 'plan_req_stale',
                        detail: `req v${req.version} > plan baseline v${plan.req_version}`,
                        actionable: !isInactiveStatus(plan.status),
                    });
                }
            }
        }
    }
    return out;
}
function getThreadStatus(thread) {
    const plans = thread.plans;
    const deliverables = thread.allDocs.filter(isDeliverable);
    if (plans.some(p => p.status === 'implementing'))
        return 'IMPLEMENTING';
    if (deliverables.length > 0 && deliverables.every(d => d.status === 'done'))
        return 'DONE';
    if (plans.some(p => p.status === 'active' || p.status === 'draft'))
        return 'ACTIVE';
    if (plans.some(p => p.status === 'blocked'))
        return 'BLOCKED';
    return 'ACTIVE';
}
/** Unset `priority` sorts after every explicitly-prioritised thread. Finite (JSON-safe). */
exports.DEFAULT_ROADMAP_PRIORITY = 1000000;
/**
 * Base roadmap status of a thread, BEFORE the dependency-blocked overlay.
 * Distinguishes `pending` (not started — no design, no plan) from `active`
 * (design or a non-done plan exists) — a distinction `getThreadStatus` collapses.
 */
function baseRoadmapStatus(thread) {
    const plans = thread.plans ?? [];
    const deliverables = (thread.allDocs ?? []).filter(isDeliverable);
    if (plans.some(p => p.status === 'implementing'))
        return 'implementing';
    if (deliverables.length > 0 && deliverables.every(d => d.status === 'done'))
        return 'done';
    if (plans.some(p => p.status === 'active' || p.status === 'draft' || p.status === 'blocked'))
        return 'active';
    if (thread.design)
        return 'active';
    return 'pending';
}
const ROADMAP_CMP = (a, b) => a.priority - b.priority ||
    (0, dates_1.compareDates)(a.created, b.created) ||
    (a.threadId < b.threadId ? -1 : a.threadId > b.threadId ? 1 : 0);
/**
 * Kahn topological sort over the non-done node set. Edges run dep → node (a
 * dependency is emitted before the thread that depends on it). Among ready nodes
 * the order is `priority`, then `created`, then `threadId` (deterministic). Nodes
 * left after the queue drains are in a cycle: returned in `cycleNodes` and appended
 * (priority-ordered) so the rest of the roadmap still renders.
 */
function topoOrder(nodes) {
    const nonDone = nodes.filter(n => n.status !== 'done');
    const byUlid = new Map();
    for (const n of nonDone)
        if (n.ulid)
            byUlid.set(n.ulid, n);
    const indeg = new Map();
    const succ = new Map();
    for (const n of nonDone) {
        indeg.set(n, 0);
        succ.set(n, []);
    }
    for (const n of nonDone) {
        for (const dep of n.dependsOn) {
            const d = byUlid.get(dep);
            if (d && d !== n) {
                indeg.set(n, (indeg.get(n) ?? 0) + 1);
                succ.get(d).push(n);
            }
        }
    }
    const ready = nonDone.filter(n => (indeg.get(n) ?? 0) === 0);
    const ordered = [];
    const placed = new Set();
    while (ready.length > 0) {
        ready.sort(ROADMAP_CMP);
        const n = ready.shift();
        ordered.push(n);
        placed.add(n);
        for (const m of succ.get(n)) {
            indeg.set(m, (indeg.get(m) ?? 0) - 1);
            if ((indeg.get(m) ?? 0) === 0)
                ready.push(m);
        }
    }
    const cycleNodes = nonDone.filter(n => !placed.has(n)).sort(ROADMAP_CMP);
    return { ordered: [...ordered, ...cycleNodes], cycleNodes };
}
/** Completed plans across all live weaves, newest first, keyed on the done-doc date. */
function buildHistory(state) {
    const shipped = [];
    for (const weave of state.weaves ?? []) {
        for (const thread of weave.threads ?? []) {
            for (const plan of thread.plans ?? []) {
                if (plan.status !== 'done')
                    continue;
                const done = (thread.dones ?? []).find(d => d.parent_id === plan.id);
                const date = done?.created ?? plan.updated ?? plan.created ?? '';
                shipped.push({
                    planId: plan.id,
                    planTitle: plan.title,
                    threadId: thread.id,
                    weaveId: weave.id,
                    date,
                    release: plan.actual_release ?? null,
                });
            }
        }
    }
    // Newest-first, comparing by parsed epoch (not raw string) so a date-only and a
    // full-ISO value of the same day order correctly — the roadmap History fix.
    return shipped.sort((a, b) => (0, dates_1.compareDates)(b.date, a.date));
}
/**
 * Build the derived cross-weave roadmap from state. Pure; reads never mutate.
 * Archived threads (and their `th_` ULIDs) count as satisfied dependency targets.
 */
function buildRoadmap(state) {
    const diagnostics = [];
    const nodes = [];
    // ULID → base status, used to decide whether a dependency is satisfied (done).
    const baseByUlid = new Map();
    // Archived threads are closed work — their manifests satisfy dependencies.
    for (const thread of state.archivedThreads ?? []) {
        const ulid = thread.manifest?.id;
        if (ulid)
            baseByUlid.set(ulid, 'done');
    }
    for (const weave of state.weaves ?? []) {
        for (const thread of weave.threads ?? []) {
            const base = baseRoadmapStatus(thread);
            const ulid = thread.manifest?.id ?? null;
            if (ulid)
                baseByUlid.set(ulid, base);
            nodes.push({
                threadId: thread.id,
                ulid,
                weaveId: weave.id,
                title: thread.manifest?.title ?? thread.idea?.title ?? thread.id,
                status: base,
                dependsOn: [...(thread.manifest?.depends_on ?? [])],
                blockedOn: [],
                priority: thread.manifest?.priority ?? exports.DEFAULT_ROADMAP_PRIORITY,
                created: thread.manifest?.created ?? thread.idea?.created ?? '',
            });
            if (!thread.manifest) {
                diagnostics.push({
                    kind: 'missing_manifest',
                    weaveId: weave.id,
                    threadId: thread.id,
                    detail: `Thread ${weave.id}/${thread.id} has no thread.md — run \`loom migrate\`.`,
                });
            }
        }
    }
    // Dependency-blocked overlay: a non-done thread with any unsatisfied dep → blocked.
    for (const node of nodes) {
        if (node.status === 'done')
            continue;
        for (const dep of node.dependsOn) {
            const target = baseByUlid.get(dep);
            if (target === undefined) {
                node.blockedOn.push(dep);
                diagnostics.push({
                    kind: 'dangling_dep',
                    weaveId: node.weaveId,
                    threadId: node.threadId,
                    detail: `depends_on references unknown thread ${dep}`,
                    refs: [dep],
                });
            }
            else if (target !== 'done') {
                node.blockedOn.push(dep);
            }
        }
        if (node.blockedOn.length > 0)
            node.status = 'blocked';
    }
    const { ordered, cycleNodes } = topoOrder(nodes);
    for (const n of cycleNodes) {
        diagnostics.push({
            kind: 'cycle',
            weaveId: n.weaveId,
            threadId: n.threadId,
            detail: `Thread ${n.weaveId}/${n.threadId} is in a depends_on cycle`,
            refs: n.dependsOn,
        });
    }
    const history = buildHistory(state);
    return {
        roadmap: ordered,
        history,
        currentRelease: (0, versionUtils_1.maxVersion)(history.map(h => h.release)),
        diagnostics,
    };
}
